import React, { createContext, useState, useContext, useMemo, useCallback } from 'react';

const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
    const [modals, setModals] = useState([]);

    const openPopup = useCallback(({
        widthClass = 'w-md',
        title = '',
        content,
        actions = [],
        closeOnBackdropClick = true,
        isDraggable = true,
        isAlert = false,
        duration = 0,
        animation = 'slide',    // 'slide', 'fade', 'zoom', 'bounce'
        style = '',               // success, error, warning, info        
    }) => {
        const id = Date.now().toString() + Math.random().toString(36).substr(2, 5);
        const newModal = {
            id, widthClass, title, content, actions, animation, closeOnBackdropClick, isDraggable, isAlert, style, duration,
        };
        setModals(prev => [...prev, newModal]);
        return id;
    }, []);

    const closePopup = useCallback((id = null) => {
        setModals(prev => {
            if (prev.length === 0) return prev;
            if (id) {
                return prev.filter(m => m.id !== id);
            }
            // If no ID is provided, pop the top-most modal
            return prev.slice(0, -1);
        });
    }, []);

    const showAlert = useCallback((config) => {
        openPopup({
            ...config,
            isAlert: true
        });
    }, [openPopup]);

    // Backwards compatibility properties
    const isOpen = modals.length > 0;
    const modalContent = isOpen ? modals[modals.length - 1] : {
        widthClass: '', title: '', content: null, actions: [], closeOnBackdropClick: true, isDraggable: true, isAlert: false, duration: 0, animation: 'slide', style: ''
    };

    const setModalLoading = useCallback((isLoading) => {
        setModals(prev => {
            if (prev.length === 0) return prev;
            const newModals = [...prev];
            const topModal = newModals[newModals.length - 1];
            newModals[newModals.length - 1] = { ...topModal, isLoading };
            return newModals;
        });
    }, []);

    const value = useMemo(() => ({
        modals, // Exporting the full array for the Modal component
        isOpen,
        openPopup,
        closePopup,
        showAlert,
        modalContent,
        setModalLoading
    }), [modals, isOpen, openPopup, closePopup, showAlert, modalContent, setModalLoading]);

    return (
        <ModalContext.Provider value={value}>
            {children}
        </ModalContext.Provider>);
};